import React, { useEffect, useState, useContext } from 'react';

import { Modal, Form, OverlayTrigger, Button } from 'react-bootstrap';

import { searchResults, documentDefinition } from '../../types/Search';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { useOktaAuth } from '@okta/okta-react';
import Popover from 'react-bootstrap/Popover';
import '../../assets/css/Search.css';
import IFramePrinter from '../utility/IFramePrinter';
import '../../assets/css/SearchResultsModal.css';
import TooltipContent from './TooltipContent';

import ImageDataContext from '../../providers/imageData-context';
import ImageService from '../../services/ImageService';
import SearchResultsColumnHeader from './search/SearchResultsColumnHeader';

export interface Props {
    show: boolean;
    handleShow: any;
    handleClose: any;
    handleCloseWithRefresh: any;
    searchimageReq: any;
    searchResultsData: searchResults;
    displayInfo: string;
    displayInfoNames: string;
    selectedCountyName: string;
    refreshHistoryCallback: any;
    messageForSearchResults: string;
}

export const SearchResultsModal: React.FC<Props> = ({ ...Props }) => {
    const imageDataCtx = useContext(ImageDataContext);

    const { oktaAuth, authState } = useOktaAuth();
    const [checkedall, setCheckedAll] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [error, setError] = useState<AxiosError>();
    const [isError, setIsError] = useState(false);
    const [response, setResponse] = useState<AxiosResponse>();

    const [currentPageIdx, setCurrentPageIdx] = useState<number>(1);
    const [currentData, setCurrentData] = useState<documentDefinition[]>([]);
    const [currentSortColumn, setCurrentSortColumn] = useState<string>('');
    const [currentSortDirection, setCurrentSortDirection] = useState<string>('asc');

    const RESTRICT_SELECTION: number = 50;
    const RESTRICT_MESSAGE = `Not all images were selected. Maximum selection is limited to ${RESTRICT_SELECTION.toString()}.`;
    const RESTRICT_MESSAGE_SHORT = `Maximum selection is limited to ${RESTRICT_SELECTION.toString()}.`;

    var uid: string = '';
    var accessToken: string = '';
    var cid: string = '';
    var groupName: string = '';
    if (authState.isAuthenticated) {
        uid = authState.accessToken.claims?.uid;
        cid = authState.accessToken.claims?.cid;
        accessToken = authState.accessToken.accessToken;
    } else {
        throw new Error('User Unauthorized');
    }

    function validationFile() {
        const pickedList = currentData?.filter((item) => item.isChecked === true);
        var checkedCount = pickedList.length;

        if (checkedCount == 0) {
            setAlertMessage('No images were selected. Please select at least one image.');
            setShowAlert(true);
            return false;
        }

        return true;
    }

    function recordFound(recordCount: number) {
        if (recordCount == 0) {
            setAlertMessage('Insufficient search parameters. Please enter valid search criteria.');
            setShowAlert(true);
        }
    }

    const documentsToLoadPerPage = 250;
    const noOfRecordsFound = Props.searchResultsData?.results.length;
    const pages = Math.ceil(noOfRecordsFound / documentsToLoadPerPage);

    useEffect(() => {
        recordFound(noOfRecordsFound);

        setCurrentPageIdx(1);
        const pagedData = getResults(1, documentsToLoadPerPage, noOfRecordsFound);
        setCurrentData(pagedData);
    }, [Props.searchResultsData]);

    useEffect(() => {
        const pagedData = getResults(currentPageIdx, documentsToLoadPerPage, noOfRecordsFound);
        setCurrentData(pagedData);
    }, [currentPageIdx, currentSortColumn, currentSortDirection]);

    const getResults = (currentPage: number, pageSize: number, totalRecords: number) => {
        const startIndex = 0;
        let endIndex = currentPage * pageSize - 1;

        if (endIndex > totalRecords) {
            endIndex = totalRecords - 1;
        }

        const alphaNumericSort = (arr: documentDefinition[]) => {
            const sorter = (a: documentDefinition, b: documentDefinition) => {
                let aValue = a.imageSubTypeAbbreviation;
                let bValue = b.imageSubTypeAbbreviation;

                if (currentSortColumn != 'Image Type') {
                    const aAttribute = a.attributes.find((x) => x.description == currentSortColumn);
                    const bAttribute = b.attributes.find((x) => x.description == currentSortColumn);
                    aValue = aAttribute ? aAttribute.value : '';
                    bValue = bAttribute ? bAttribute.value : '';
                }

                const isNumber = (v: any) => (+v).toString() === v;
                const aPart = aValue.match(/\d+|\D+/g);
                const bPart = bValue.match(/\d+|\D+/g);

                const isAscending = currentSortDirection === 'asc';

                if (aPart == null && bPart == null) {
                    return 0;
                }
                if (aPart == null) return 1;
                if (bPart == null) return -1;

                let i = 0;
                let len = Math.min(aPart.length, bPart.length);
                while (i < len && aPart[i] === bPart[i]) {
                    i++;
                }
                if (i === len) {
                    return isAscending ? aPart.length - bPart.length : bPart.length - aPart.length;
                }
                if (isNumber(aPart[i]) && isNumber(bPart[i])) {
                    return isAscending
                        ? Number(aPart[i]) - Number(bPart[i])
                        : Number(bPart[i]) - Number(aPart[i]);
                }
                return isAscending
                    ? aPart[i].localeCompare(bPart[i])
                    : bPart[i].localeCompare(aPart[i]);
            };
            arr.sort(sorter);
            return arr;
        };

        const sortedResults = currentSortColumn
            ? alphaNumericSort(Props.searchResultsData?.results)
            : Props.searchResultsData?.results;

        const resultData = sortedResults.filter(
            (item, idx) => idx >= startIndex && idx <= endIndex
        );
        return resultData;
    };

    const handleLoadMore = () => {
        setCurrentPageIdx((prevState) => {
            return prevState + 1;
        });
    };

    const handleOk = () => {
        const pickedList = currentData?.filter((item) => item.isChecked === true);
        var checkedData = pickedList;

        if (validationFile()) {
            updateActivityHistory(checkedData, true).then(() => {
                Props.handleCloseWithRefresh();
            });
        }
    };

    const handleCheckAllChange = (e: any) => {
        const isChecked = e.currentTarget.checked;

        setShowAlert(false);
        setCheckedAll(isChecked);

        setCurrentData((prevState) => {
            let newState = [...prevState];
            if (!isChecked) {
                newState.forEach((d) => {
                    d.isChecked = false;
                });
            }
            if (isChecked) {
                const pickedList = currentData?.filter((item) => item.isChecked === true);
                let selectedCount = pickedList.length;
                newState?.forEach((d) => {
                    if (selectedCount === RESTRICT_SELECTION) {
                        setAlertMessage(RESTRICT_MESSAGE);
                        setShowAlert(true);
                        return;
                    }

                    if (!d.isChecked) {
                        d.isChecked = true;
                        selectedCount++;
                    }
                });
            }
            return newState;
        });
    };

    const handleCheckChange = (e: any, data: any) => {
        const isChecked = e.currentTarget.checked;
        const pickedList = currentData?.filter((item) => item.isChecked === true);
        let selectedCount = pickedList.length;

        if (e.currentTarget.checked && selectedCount === RESTRICT_SELECTION) {
            setCheckedAll(RESTRICT_SELECTION === selectedCount);
            return false;
        }

        setCurrentData((prevState) => {
            let newState = [...prevState];
            newState.filter((d) => d.documentName == data.documentName)[0].isChecked = isChecked;
            return newState;
        });

        e.currentTarget.checked ? selectedCount++ : selectedCount--;
        setCheckedAll(RESTRICT_SELECTION === selectedCount);

        if (selectedCount === RESTRICT_SELECTION) {
            setAlertMessage(RESTRICT_MESSAGE_SHORT);
            setShowAlert(true);
        } else {
            setShowAlert(false);
        }

        return false;
    };

    const updateActivityHistory = async (
        fileRequest: Array<documentDefinition>,
        addToImagesSection: boolean = false
    ) => {
        // We need to add unique access dates, which are just now + 1ms each for ordering purposes
        const ordered = ImageService.addUniqueAccessDates(fileRequest);
        if (addToImagesSection) {
            // If we are adding to images section that means we clicked Ok
            imageDataCtx.addImagesToImagesList(ordered);
        } else {
            // Otherwise we are saving
            imageDataCtx.addImagesToActivityHistory(ordered);
        }
    };

    const applySort = (columnName: string) => {
        if (currentSortColumn == columnName) {
            setCurrentSortDirection((currentValue) => {
                if (currentValue == 'asc') return 'desc';
                return 'asc';
            });
        } else {
            setCurrentSortColumn(columnName);
            setCurrentSortDirection('asc');
        }
    };

    return (
        <>
            {currentData && Props.show && (
                <Modal
                    show={Props.show}
                    size='xl'
                    scrollable={true}
                    aria-labelledby='search-modal-sizes-title-lg'
                >
                    <Modal.Header
                        closeButton
                        closeLabel='close'
                        onHide={Props.handleClose}
                        bsPrefix='modal-header'
                    ></Modal.Header>
                    <div className='searchBarHeader d-flex justify-content-between'>
                        <div>
                            <b>Search Results</b>
                        </div>
                    </div>
                    <Modal.Body>
                        <div>
                            <table id='tableSearchResults' className='table searchResults'>
                                <thead>
                                    <tr
                                        style={{
                                            position: 'sticky',
                                            top: '0',
                                            background: 'white',
                                        }}
                                    >
                                        <th scope='col'>
                                            <Form.Check
                                                inline
                                                label=''
                                                type='checkbox'
                                                onChange={(e) => {
                                                    handleCheckAllChange(e);
                                                }}
                                                //onClick={handleClick}
                                                checked={checkedall}
                                            />
                                        </th>
                                        <SearchResultsColumnHeader
                                            columnName={'Image Type'}
                                            sorted={currentSortColumn == 'Image Type'}
                                            sortDirection={currentSortDirection}
                                            handleClick={applySort}
                                        />

                                        {Props.searchResultsData.columns.map((column) => (
                                            <SearchResultsColumnHeader
                                                key={column.name}
                                                columnName={column.description}
                                                sorted={column.description == currentSortColumn}
                                                sortDirection={currentSortDirection}
                                                handleClick={applySort}
                                            />
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentData.map((data, i) => {
                                        return (
                                            <OverlayTrigger
                                                key={`resultsOverLay-${data.name}`}
                                                placement={'auto'}
                                                delay={{ show: 1000, hide: 0 }}
                                                trigger={['hover', 'focus']}
                                                overlay={
                                                    <Popover
                                                        id={`tooltip-${data.name}`}
                                                        className='image-info-popup'
                                                        title='Image Info'
                                                    >
                                                        <Popover.Header as='h3'>
                                                            Image Info
                                                        </Popover.Header>
                                                        <Popover.Body>
                                                            <TooltipContent
                                                                data={data}
                                                            ></TooltipContent>
                                                        </Popover.Body>
                                                    </Popover>
                                                }
                                            >
                                                <tr
                                                    key={`resultsRow-${i}`}
                                                    className={
                                                        data.isChecked ? 'tableSelected' : ''
                                                    }
                                                >
                                                    <th scope='row'>
                                                        <Form.Check
                                                            inline
                                                            label=''
                                                            type='checkbox'
                                                            onChange={(e) => {
                                                                handleCheckChange(e, data);
                                                            }}
                                                            checked={data.isChecked ?? false}
                                                            id={`checkopt-${i}`}
                                                        />
                                                    </th>
                                                    {/* <td>{Props.formats.filter((f)=>f.subType.toUpperCase() == data.attributes.filter(d=>d.name=="Subtype")[0]?.value)[0]?.subTypeAbbreviation}</td> */}
                                                    <td
                                                        style={{
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {data.imageSubTypeAbbreviation}
                                                    </td>
                                                    {Props.searchResultsData.columns.map(
                                                        (column) => (
                                                            <td key={column.name}>
                                                                {column.name.trim() != '' && (
                                                                    <span
                                                                        style={{
                                                                            whiteSpace: 'nowrap',
                                                                        }}
                                                                    >
                                                                        {
                                                                            data.attributes.filter(
                                                                                (n) =>
                                                                                    n.name.toUpperCase() ==
                                                                                    column.name.toUpperCase()
                                                                            )[0]?.value
                                                                        }
                                                                    </span>
                                                                )}
                                                            </td>
                                                        )
                                                    )}
                                                </tr>
                                            </OverlayTrigger>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </Modal.Body>

                    <Modal.Footer>
                        <div className='search-modal-footer'>
                            <div>
                                <span
                                    style={{
                                        color: 'red',
                                        fontSize: '16px',
                                        fontWeight: 'bold',
                                        visibility:
                                            Props.messageForSearchResults !== ''
                                                ? 'visible'
                                                : 'hidden',
                                    }}
                                >
                                    {Props.messageForSearchResults}
                                </span>
                                <span
                                    style={{
                                        color: 'red',
                                        fontSize: '16px',
                                        fontWeight: 'bold',
                                        visibility: showAlert == true ? 'visible' : 'hidden',
                                    }}
                                >
                                    {alertMessage}
                                </span>
                            </div>
                            <div style={{ display: 'flex' }}>
                                <Button variant='primary' onClick={(e) => handleOk()}>
                                    Ok
                                </Button>
                                <Button variant='primary' onClick={Props.handleClose}>
                                    Cancel
                                </Button>
                                <Button
                                    variant='primary'
                                    className={
                                        Props.searchResultsData.results.length !==
                                        currentData.length
                                            ? 'blockVisible'
                                            : 'noneHidden'
                                    }
                                    onClick={handleLoadMore}
                                >
                                    Load More
                                </Button>
                            </div>
                        </div>
                    </Modal.Footer>
                </Modal>
            )}
        </>
    );
};

export default SearchResultsModal;
